/**
 * Main application entry point.
 *
 * This file composes and initializes application modules. Core functionality
 * is implemented in separate modules under ./lib/ and imported here.
 * Keep this file focused on initialization and wiring, not implementation.
 */

// Side-effect import: handles method=PUT/DELETE in forms and buttons
import 'phoenix_html';

import { initTopbar } from './lib/topbar';
import { initDevTools } from './lib/dev-tools';
import { initParallax } from './lib/parallax';
import { initAOS } from './lib/aos';
import { initGTMTracking } from './lib/gtm';
import { initSchedulerLinks } from './lib/scheduler-links';
import { initClipboard } from './lib/clipboard';
import { initNav } from './lib/nav';
import { initEmailCapture } from './lib/email-capture';

// Persist browser timezone in a cookie so the server can render local
// times on the initial HTTP response (before the WebSocket connects).
if (!document.cookie.includes('_tz=')) {
  document.cookie = `_tz=${Intl.DateTimeFormat().resolvedOptions().timeZone};path=/;max-age=31536000;SameSite=Lax`;
}

// Initialize core components
initTopbar();
initDevTools();

// Initialize page-specific features
function initPageFeatures(): void {
  initNav();
  initParallax();
  initAOS();
  initGTMTracking();
  initSchedulerLinks();
  initClipboard();
  setupBookingWidget();
  initEmailCapture();
}

// Run on initial page load
document.addEventListener('DOMContentLoaded', initPageFeatures);

// Re-run on LiveView navigation
window.addEventListener('phx:page-loading-stop', initPageFeatures);

// The booking widget (calendar + slot math) is the heaviest page feature but is
// only needed once the visitor engages with it. Split it into its own chunk and
// load it on first interaction, with an idle-time fallback so it is ready even
// if no interaction events fire (e.g. assistive tech, programmatic focus).
function setupBookingWidget(): void {
  document.querySelectorAll<HTMLElement>('[data-booking-widget]').forEach((root) => {
    if (
      root.dataset['bookingPending'] === 'true' ||
      root.dataset['bookingInitialized'] === 'true'
    ) {
      return;
    }
    root.dataset['bookingPending'] = 'true';

    let loaded = false;
    const load = (): void => {
      if (loaded) return;
      loaded = true;
      root.removeEventListener('pointerdown', load);
      root.removeEventListener('focusin', load);
      void import('./lib/booking-widget').then(({ initBookingWidget }) => {
        initBookingWidget();
      });
    };

    root.addEventListener('pointerdown', load, { once: true });
    root.addEventListener('focusin', load, { once: true });
    scheduleIdle(load);
  });
}

function scheduleIdle(fn: () => void): void {
  const ric = (window as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(fn);
  } else {
    window.setTimeout(fn, 2000);
  }
}

// LiveView powers only the ops dashboards, not the marketing pages. Skip loading
// the LiveView client (the largest part of the bundle) and opening the socket
// unless a LiveView is actually rendered on the page.
function connectLiveViewIfPresent(): void {
  if (document.querySelector('[data-phx-main], [data-phx-session]') === null) return;

  void Promise.all([import('./lib/live-socket'), import('./hooks')]).then(
    ([{ createLiveSocket }, { hooks }]) => {
      const liveSocket = createLiveSocket({ hooks });
      liveSocket.connect();

      // Expose for console debugging:
      //   liveSocket.enableDebug()
      //   liveSocket.enableLatencySim(1000)
      //   liveSocket.disableLatencySim()
      window.liveSocket = liveSocket;
    },
  );
}

connectLiveViewIfPresent();
